import {
  getAppointmentStaffRequiredTasks,
  isAppointmentResourceOnlyTask,
} from "../scheduling/appointmentStaffRequiredTasks";
import {
  resolveAppointmentSchedulingContext,
  type AppointmentSchedulingContextFailureReason,
  type AppointmentSchedulingContextResult,
  type AppointmentSchedulingContextSources,
} from "@/app/booking/adapters/appointmentSchedulingContext";
import type { SpecialistAppointmentSchedulingCatalogSource } from "@/app/booking/adapters/specialistAppointmentSchedulingContext";
import type { LocationAppointmentCatalog } from "@/app/booking/adapters/locationAppointmentCatalog";
import {
  validateAppointmentBookingDraft,
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
  type AppointmentServiceLineDraft,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  validateAppointmentSchedulingDraft,
  type AppointmentSchedulingDraftV1,
} from "@/app/booking/core/appointmentSchedulingDraft";
import {
  appointmentTerminalPresentationCoversDraft,
  validateAppointmentTerminalPresentationSnapshot,
  type AppointmentTerminalPresentationSnapshotV1,
} from "@/app/booking/core/appointmentTerminalPresentation";
import { appointmentSchedulingRuntime } from "@/app/booking/scheduling/appointmentSchedulingRuntime";
import {
  appointmentDraftRequiresStaffChoiceEvidence,
  appointmentStaffChoiceEvidenceCoversSelectedSlot,
  appointmentStaffChoiceEvidenceMatchesDraft,
  createAppointmentStaffChoiceEvidence,
  type AppointmentProviderVisibleStaffPresentation,
  type AppointmentStaffChoiceEvidenceV2,
} from "@/app/booking/scheduling/appointmentStaffChoiceEvidence";
import { buildAppointmentSchedulePlan } from "@/app/booking/scheduling/appointmentSchedulePlan";
import type {
  AppointmentProfessionalSelectionResolution,
  AppointmentProfessionalSelectionStrategy,
} from "@/app/booking/scheduling/appointmentProfessionalSelection";
import { getEditableAppointmentStaffTasks } from "@/app/booking/scheduling/appointmentStaffPreferences";
import {
  appointmentTimeProfessionalMutationEvidenceCoversSelectedSlot,
  appointmentTimeProfessionalMutationEvidenceMatchesDraft,
  haveExactAppointmentTimeProfessionalMutationAuthority,
  isAppointmentTimeProfessionalMutationEvidenceCurrent,
  type AppointmentTimeProfessionalMutationEvidenceV1,
} from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";

type ResolvedAppointmentSchedulingContext = Extract<
  AppointmentSchedulingContextResult,
  { ok: true }
>;

export interface AppointmentCompletionPresentation {
  professionals: readonly { name: string; userLocationId: string }[];
  specialist: { name: string } | null;
  workplace: {
    name: string;
    address: string;
    marketplace: {
      imageAlt: string;
      imageSrc: string | null;
      images?: readonly { src: string }[] | null;
      /** Prospective page-only marketplace aggregate; never frozen as receipt authority. */
      rating?: number | null;
      reviewCount?: number;
    } | null;
  };
}

export type AppointmentCompletionScheduling = {
  services: readonly { name: string; serviceId: string }[];
} & Partial<
  Omit<ResolvedAppointmentSchedulingContext["scheduling"], "services">
>;

export type AppointmentCompletionContextFailureReason =
  | AppointmentSchedulingContextFailureReason
  | "invalid-scheduling-draft"
  | "scheduling-source-mismatch"
  | "unsupported-scheduling-transition";

export type AppointmentCompletionContextResult =
  | {
      ok: true;
      /**
       * Exact current origin assignment/catalog rebuilt from the immutable
       * Step-1 source. Completion routes consume this context instead of
       * trusting copied presentation or URL state.
       */
      specialistContext: AppointmentCompletionScheduling;
      presentation: AppointmentCompletionPresentation;
      /** Defensive, fully validated Step-2 snapshot. */
      schedulingDraft: AppointmentSchedulingDraftV1;
      /**
       * Exact provider-visible choices used to authorize Location or Group
       * staff selections. Scheduling-only staff never enter this snapshot.
       */
      staffChoiceEvidence: AppointmentStaffChoiceEvidenceV2 | null;
      timeProfessionalMutationEvidence: AppointmentTimeProfessionalMutationEvidenceV1 | null;
    }
  | {
      ok: false;
      reason: AppointmentCompletionContextFailureReason;
    };

export type SpecialistAppointmentCompletionContextFailureReason =
  AppointmentCompletionContextFailureReason;
export type SpecialistAppointmentCompletionContextResult =
  AppointmentCompletionContextResult;

function buildHistoricalCompletionPresentation(
  snapshot: AppointmentTerminalPresentationSnapshotV1,
): AppointmentCompletionPresentation {
  return {
    specialist: snapshot.specialist ? { name: snapshot.specialist.name } : null,
    workplace: {
      name: snapshot.workplace.name,
      address: snapshot.workplace.address,
      marketplace: snapshot.workplace.marketplace
        ? {
            ...snapshot.workplace.marketplace,
            images: snapshot.workplace.marketplace.images.map((image) => ({
              ...image,
            })),
          }
        : null,
    },
    professionals:
      snapshot.professionals?.map((professional) => ({ ...professional })) ??
      [],
  };
}

function buildHistoricalCompletionScheduling(
  presentationSnapshot: AppointmentTerminalPresentationSnapshotV1,
): AppointmentCompletionScheduling {
  return {
    services: presentationSnapshot.services.map((service) => ({
      serviceId: service.serviceId,
      name: service.name,
    })),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasSameStructure(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => hasSameStructure(value, right[index]))
    );
  }

  if (!isRecord(left) || !isRecord(right)) {
    return false;
  }

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();

  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) =>
        key === rightKeys[index] && hasSameStructure(left[key], right[key]),
    )
  );
}

function getCatalogComparableLine(line: AppointmentServiceLineDraft) {
  return {
    lineId: line.lineId,
    selectionOrder: line.selectionOrder,
    scheduleOrder: line.scheduleOrder,
    selection:
      line.selection.kind === "service"
        ? line.selection
        : {
            ...line.selection,
            bundleItems: line.selection.bundleItems.map((item) => ({
              bundleItemId: item.bundleItemId,
              serviceId: item.serviceId,
              offeringId: item.offeringId,
              variantId: item.variantId,
              addOns: item.addOns,
              staffAutoAssignmentEligible: item.staffAutoAssignmentEligible,
              timeSegments: item.timeSegments,
              onlineBooking: item.onlineBooking,
            })),
          },
    staffAutoAssignmentEligible: line.staffAutoAssignmentEligible,
    onlineBooking: line.onlineBooking,
    quote: line.quote,
    snapshot: line.snapshot,
  };
}

function getJourneyComparable(draft: AppointmentBookingDraftV1) {
  return {
    schemaVersion: draft.schemaVersion,
    draftId: draft.draftId,
    kind: draft.kind,
    mode: draft.mode,
    locationId: draft.locationId,
    timeZone: draft.timeZone,
    entryPoint: draft.entryPoint,
    staffAssignmentPolicy: draft.staffAssignmentPolicy,
    organizerParticipantId: draft.organizerParticipantId,
    groupPolicy: draft.groupPolicy,
  };
}

function workingDraftMatchesCatalogLineage(input: {
  sourceDraft: AppointmentBookingDraftV1;
  schedulingDraft: AppointmentBookingDraftV1;
}) {
  const { sourceDraft, schedulingDraft } = input;

  if (sourceDraft.mode !== schedulingDraft.mode) {
    return false;
  }

  if (
    !hasSameStructure(
      getJourneyComparable(sourceDraft),
      getJourneyComparable(schedulingDraft),
    )
  ) {
    return false;
  }

  if (sourceDraft.participants.length !== schedulingDraft.participants.length) {
    return false;
  }

  return schedulingDraft.participants.every((participant, index) => {
    const sourceParticipant = sourceDraft.participants[index];

    if (
      !sourceParticipant ||
      participant.participantId !== sourceParticipant.participantId ||
      participant.role !== sourceParticipant.role ||
      !hasSameStructure(participant.identity, sourceParticipant.identity)
    ) {
      return false;
    }

    const sourceLines = [...sourceParticipant.serviceLines].sort(
      (left, right) => left.selectionOrder - right.selectionOrder,
    );
    const scheduledLines = [...participant.serviceLines].sort(
      (left, right) => left.selectionOrder - right.selectionOrder,
    );

    return (
      sourceLines.length === scheduledLines.length &&
      scheduledLines.every((line, lineIndex) =>
        hasSameStructure(
          getCatalogComparableLine(line),
          getCatalogComparableLine(sourceLines[lineIndex]!),
        ),
      )
    );
  });
}

function staffPreferencesAreProviderSupported(input: {
  sourceDraft: AppointmentBookingDraftV1;
  workingDraft: AppointmentBookingDraftV1;
  staffChoiceEvidence: AppointmentStaffChoiceEvidenceV2;
}) {
  const sourcePlanResult = buildAppointmentSchedulePlan(input.sourceDraft);
  const workingPlanResult = buildAppointmentSchedulePlan(input.workingDraft);

  if (!sourcePlanResult.ok || !workingPlanResult.ok) {
    return false;
  }

  const sourceTaskById = new Map(
    sourcePlanResult.plan.tasks.map((task) => [task.taskId, task]),
  );
  if (
    !appointmentStaffChoiceEvidenceMatchesDraft({
      draft: input.workingDraft,
      evidence: input.staffChoiceEvidence,
    })
  ) {
    return false;
  }

  const manualChoiceIdsByTaskId = new Map(
    input.staffChoiceEvidence.taskChoices.map((choice) => [
      choice.taskId,
      new Set(choice.options.map(({ userLocationId }) => userLocationId)),
    ]),
  );
  const autoAssignmentIdsByTaskId = new Map(
    input.staffChoiceEvidence.taskChoices.map((choice) => [
      choice.taskId,
      new Set(choice.autoAssignableUserLocationIds),
    ]),
  );

  if (
    sourcePlanResult.plan.tasks.length !== workingPlanResult.plan.tasks.length
  ) {
    return false;
  }

  return workingPlanResult.plan.tasks.every((workingTask) => {
    const sourceTask = sourceTaskById.get(workingTask.taskId);

    if (!sourceTask) {
      return false;
    }

    if (
      isAppointmentResourceOnlyTask(sourceTask) ||
      isAppointmentResourceOnlyTask(workingTask)
    ) {
      return (
        isAppointmentResourceOnlyTask(sourceTask) &&
        isAppointmentResourceOnlyTask(workingTask) &&
        hasSameStructure(sourceTask, workingTask)
      );
    }

    const sourcePreference = sourceTask.staffPreference;
    const workingPreference = workingTask.staffPreference;

    if (
      sourcePreference.kind === "specific" &&
      sourcePreference.source === "specialist-entry"
    ) {
      return hasSameStructure(sourcePreference, workingPreference);
    }

    if (workingPreference.kind === "any") {
      return (
        input.staffChoiceEvidence.professionalSelection !== null &&
        workingTask.staffAutoAssignmentEligible &&
        workingPlanResult.plan.staffAssignmentPolicy.allowAnyProfessional &&
        (autoAssignmentIdsByTaskId.get(workingTask.taskId)?.size ?? 0) > 0
      );
    }

    if (
      workingPreference.kind !== "specific" ||
      workingPreference.source !== "customer-selection"
    ) {
      return false;
    }

    return (
      manualChoiceIdsByTaskId
        .get(workingTask.taskId)
        ?.has(workingPreference.userLocationId) ?? false
    );
  });
}

function validateCurrentProfessionalSelectionStrategy(input: {
  workingDraft: AppointmentBookingDraftV1;
  selectedStrategy: AppointmentProfessionalSelectionStrategy;
  resolution: Extract<
    AppointmentProfessionalSelectionResolution,
    { ok: true; origin: "location" }
  >;
}) {
  if (
    input.workingDraft.entryPoint.kind !== "location-services" ||
    input.resolution.origin !== "location"
  ) {
    return false;
  }

  const planResult = buildAppointmentSchedulePlan(input.workingDraft);

  if (!planResult.ok) {
    return false;
  }

  const tasks = getEditableAppointmentStaffTasks(planResult.plan);
  const anyStrategy = input.resolution.strategies.find(
    ({ kind }) => kind === "any-professional",
  );

  if (
    input.selectedStrategy === "any-professional" &&
    anyStrategy &&
    tasks.length > 0 &&
    tasks.every(({ staffPreference }) => staffPreference.kind === "any")
  ) {
    return true;
  }

  const commonStrategy = input.resolution.strategies.find(
    (strategy) => strategy.kind === "common-professional",
  );
  const selectedIds = new Set(
    tasks.flatMap(({ staffPreference }) =>
      staffPreference.kind === "specific" &&
      staffPreference.source === "customer-selection"
        ? [staffPreference.userLocationId]
        : [],
    ),
  );
  const selectedId = selectedIds.size === 1 ? [...selectedIds][0] : null;

  if (
    input.selectedStrategy === "common-professional" &&
    commonStrategy?.kind === "common-professional" &&
    selectedId &&
    tasks.every(
      ({ staffPreference }) =>
        staffPreference.kind === "specific" &&
        staffPreference.source === "customer-selection",
    ) &&
    commonStrategy.options.some(
      ({ userLocationId }) => userLocationId === selectedId,
    )
  ) {
    return true;
  }

  const perTaskStrategy = input.resolution.strategies.find(
    (strategy) => strategy.kind === "per-task",
  );

  return (
    input.selectedStrategy === "per-task" &&
    perTaskStrategy?.kind === "per-task" &&
    perTaskStrategy.currentSelectionIsCompleteAndFeasible &&
    perTaskStrategy.tasks.every((resolvedTask) => {
      const task = tasks.find(({ taskId }) => taskId === resolvedTask.taskId);
      const preference = task?.staffPreference;

      return preference?.kind === "any"
        ? resolvedTask.choices.some(
            (choice) => choice.kind === "any-professional",
          )
        : preference?.kind === "specific" &&
            preference.source === "customer-selection"
          ? resolvedTask.choices.some(
              (choice) =>
                choice.kind === "specific-professional" &&
                choice.userLocationId === preference.userLocationId,
            )
          : false;
    })
  );
}

export function resolveCurrentAppointmentStaffChoiceEvidence(input: {
  workingDraft: AppointmentBookingDraftV1;
  presentations: readonly AppointmentProviderVisibleStaffPresentation[];
  locationAppointmentCatalog?: LocationAppointmentCatalog | null;
  selectedProfessionalStrategy?: AppointmentProfessionalSelectionStrategy | null;
}) {
  const planResult = buildAppointmentSchedulePlan(input.workingDraft);

  if (!planResult.ok) {
    return null;
  }

  const manualChoices = appointmentSchedulingRuntime.manualStaffChoice.resolve({
    plan: planResult.plan,
    presentations: input.presentations,
  });

  if (!manualChoices.ok) {
    return null;
  }

  if (input.workingDraft.entryPoint.kind !== "location-services") {
    return createAppointmentStaffChoiceEvidence({
      draft: input.workingDraft,
      providerId: appointmentSchedulingRuntime.manualStaffChoice.providerId,
      evidenceVersion:
        appointmentSchedulingRuntime.manualStaffChoice.evidenceVersion,
      professionalSelection: null,
      taskChoices: manualChoices.taskChoices,
    });
  }

  if (!input.locationAppointmentCatalog) {
    return null;
  }

  const professionalSelection =
    appointmentSchedulingRuntime.professionalSelection.resolve({
      draft: input.workingDraft,
      presentations: input.presentations,
      locationEligibilityPublication:
        input.locationAppointmentCatalog.eligibilityPublication,
      orderedCatalogOfferingIds: input.locationAppointmentCatalog.offerings.map(
        ({ offeringId }) => offeringId,
      ),
    });

  if (
    !professionalSelection ||
    professionalSelection.resolution.ok !== true ||
    professionalSelection.resolution.origin !== "location" ||
    !input.selectedProfessionalStrategy ||
    !validateCurrentProfessionalSelectionStrategy({
      workingDraft: input.workingDraft,
      resolution: professionalSelection.resolution,
      selectedStrategy: input.selectedProfessionalStrategy,
    })
  ) {
    return null;
  }

  const taskPoolById = new Map(
    professionalSelection.evidence.taskPools.map((pool) => [pool.taskId, pool]),
  );
  const presentationById = new Map(
    input.presentations.map((presentation) => [
      presentation.userLocationId,
      presentation,
    ]),
  );

  return createAppointmentStaffChoiceEvidence({
    draft: input.workingDraft,
    providerId: appointmentSchedulingRuntime.manualStaffChoice.providerId,
    evidenceVersion:
      appointmentSchedulingRuntime.manualStaffChoice.evidenceVersion,
    professionalSelection: {
      providerId: professionalSelection.evidence.providerId,
      evidenceVersion: professionalSelection.evidence.evidenceVersion,
      staffEligibilityProviderId:
        professionalSelection.evidence.staffEligibilityProviderId,
      staffEligibilityEvidenceVersion:
        professionalSelection.evidence.staffEligibilityEvidenceVersion,
      locationEligibilityPublication:
        professionalSelection.evidence.locationEligibilityPublication,
      selectedStrategy: input.selectedProfessionalStrategy,
    },
    allocationPresentationEvidence:
      professionalSelection.evidence.allocationPresentationEvidence,
    taskChoices: professionalSelection.evidence.taskPools.map((pool) => ({
      taskId: pool.taskId,
      options: pool.directlySelectableUserLocationIds.flatMap(
        (userLocationId) => {
          const presentation = presentationById.get(userLocationId);

          return presentation ? [presentation] : [];
        },
      ),
      autoAssignableUserLocationIds:
        taskPoolById.get(pool.taskId)?.autoAssignableUserLocationIds ?? [],
    })),
  });
}

/**
 * Re-resolves the complete Step-2 publication that owns staff-specific quote
 * authority. The compact staff receipt alone deliberately cannot authorize a
 * Waitlist price on resume or final mutation.
 */
export function resolveCurrentAppointmentProfessionalSelectionEvidence(input: {
  workingDraft: AppointmentBookingDraftV1;
  presentations: readonly AppointmentProviderVisibleStaffPresentation[];
  locationAppointmentCatalog?: LocationAppointmentCatalog | null;
  selectedProfessionalStrategy?: AppointmentProfessionalSelectionStrategy | null;
}) {
  if (
    input.workingDraft.entryPoint.kind !== "location-services" ||
    !input.locationAppointmentCatalog ||
    !input.selectedProfessionalStrategy
  ) {
    return null;
  }
  const publication =
    appointmentSchedulingRuntime.professionalSelection.resolve({
      draft: input.workingDraft,
      presentations: input.presentations,
      locationEligibilityPublication:
        input.locationAppointmentCatalog.eligibilityPublication,
      orderedCatalogOfferingIds:
        input.locationAppointmentCatalog.offerings.map(
          ({ offeringId }) => offeringId,
        ),
    });
  return publication?.resolution.ok === true &&
    publication.resolution.origin === "location" &&
    validateCurrentProfessionalSelectionStrategy({
      workingDraft: input.workingDraft,
      resolution: publication.resolution,
      selectedStrategy: input.selectedProfessionalStrategy,
    })
    ? publication.evidence
    : null;
}

/**
 * Rebuilds the exact current manual-staff provider evidence and compares it
 * atomically with the frozen handoff evidence. This is intentionally stricter
 * than graph-only validation: provider/version, option eligibility, order,
 * and provider-visible presentation must all still be current before a
 * stored Location or Group branch can become actionable again.
 */
export function appointmentStaffChoiceEvidenceMatchesCurrentRuntime(input: {
  workingDraft: AppointmentBookingDraftV1;
  staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
  presentations: readonly AppointmentProviderVisibleStaffPresentation[];
  locationAppointmentCatalog?: LocationAppointmentCatalog | null;
}) {
  if (!appointmentDraftRequiresStaffChoiceEvidence(input.workingDraft)) {
    return input.staffChoiceEvidence == null;
  }

  const currentEvidence = resolveCurrentAppointmentStaffChoiceEvidence({
    workingDraft: input.workingDraft,
    presentations: input.presentations,
    locationAppointmentCatalog: input.locationAppointmentCatalog,
    selectedProfessionalStrategy:
      input.staffChoiceEvidence?.professionalSelection?.selectedStrategy ??
      null,
  });

  return (
    input.staffChoiceEvidence != null &&
    currentEvidence != null &&
    hasSameStructure(input.staffChoiceEvidence, currentEvidence)
  );
}

/**
 * Re-resolves the Time-stage publication from the current scheduling
 * context and compares its exact non-temporal authority with the frozen
 * evidence. Stored evidence never self-authorizes a Waitlist, completion, or
 * resume boundary.
 */
export function appointmentTimeProfessionalMutationEvidenceMatchesCurrentRuntime(input: {
  workingDraft: AppointmentBookingDraftV1;
  evidence: AppointmentTimeProfessionalMutationEvidenceV1;
  context: ResolvedAppointmentSchedulingContext;
  /**
   * The immutable boundary at which the frozen evidence authorized this
   * draft. Omit only for an active pre-mutation flow that must be fresh now.
   */
  evaluatedAtEpochMs?: number;
}) {
  return Boolean(
    resolveCurrentAppointmentTimeProfessionalMutationEvidence(input),
  );
}

/**
 * Resolves a fresh Time-stage evidence instance while requiring the complete
 * non-temporal provider authority to remain byte-exact. This is the only
 * supported way for a long-lived post-Time journey to rotate an expired
 * evidence TTL without authorizing changed staff pools, quotes or policy.
 */
export function resolveCurrentAppointmentTimeProfessionalMutationEvidence(input: {
  workingDraft: AppointmentBookingDraftV1;
  evidence: AppointmentTimeProfessionalMutationEvidenceV1;
  context: ResolvedAppointmentSchedulingContext;
  evaluatedAtEpochMs?: number;
}) {
  const presentations = input.context.presentation.professionals.map(
    (professional) => ({
      userLocationId: professional.userLocationId,
      name: professional.name,
      description: professional.role ?? professional.category,
    }),
  );
  const locationCatalog = input.context.locationAppointmentCatalog;
  const currentPublication =
    appointmentSchedulingRuntime.timeProfessionalMutation.resolve({
      draft: input.workingDraft,
      ...(locationCatalog
        ? {
            presentations,
            locationEligibilityPublication:
              locationCatalog.eligibilityPublication,
            orderedCatalogOfferingIds: locationCatalog.offerings.map(
              ({ offeringId }) => offeringId,
            ),
          }
        : {}),
    });

  return (
    currentPublication &&
    isAppointmentTimeProfessionalMutationEvidenceCurrent({
      evidence: currentPublication.evidence,
      expectedProviderIdentity: {
        providerId:
          appointmentSchedulingRuntime.timeProfessionalMutation.providerId,
        evidenceVersion:
          appointmentSchedulingRuntime.timeProfessionalMutation.evidenceVersion,
      },
    }) &&
    haveExactAppointmentTimeProfessionalMutationAuthority(
      currentPublication.evidence,
      input.evidence,
    )
      ? currentPublication.evidence
      : null
  );
}

/**
 * Accepts only scheduling-owned changes that may legitimately differ from
 * the immutable Step-1 source. Time mutation is independent of origin and
 * requires exact current per-task authority; empty availability never grants
 * or rewrites staff. Without Time evidence, the established Step-2 authority
 * remains the only Location/Group transition path.
 */
export function isSupportedAppointmentWorkingDraftTransition(input: {
  sourceDraft: AppointmentBookingDraftV1;
  workingDraft: AppointmentBookingDraftV1;
  staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
  timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  evaluatedAtEpochMs?: number;
}) {
  const sourceValidation = validateAppointmentStepOneBookingDraft(
    input.sourceDraft,
  );
  const workingValidation = validateAppointmentBookingDraft(input.workingDraft);

  if (!sourceValidation.valid || !workingValidation.valid) {
    return false;
  }

  const sourceDraft = sourceValidation.draft;
  const workingDraft = workingValidation.draft;
  const timeProfessionalMutationEvidence =
    input.timeProfessionalMutationEvidence ?? null;

  if (
    !workingDraftMatchesCatalogLineage({
      sourceDraft,
      schedulingDraft: workingDraft,
    })
  ) {
    return false;
  }

  if (timeProfessionalMutationEvidence) {
    return appointmentTimeProfessionalMutationEvidenceMatchesDraft({
      draft: workingDraft,
      evidence: timeProfessionalMutationEvidence,
      locationEligibilityPublication:
        timeProfessionalMutationEvidence.locationEligibilityPublication,
      expectedProviderIdentity: {
        providerId: timeProfessionalMutationEvidence.providerId,
        evidenceVersion: timeProfessionalMutationEvidence.evidenceVersion,
      },
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
    });
  }

  if (
    sourceDraft.entryPoint.kind === "specialist-services" &&
    sourceDraft.mode === "individual"
  ) {
    const workingMatchesSource = hasSameStructure(workingDraft, sourceDraft);

    return (
      input.staffChoiceEvidence == null &&
      workingDraft.mode === "individual" &&
      workingMatchesSource
    );
  }

  const sourcePlan = buildAppointmentSchedulePlan(sourceDraft);
  if (
    sourceDraft.entryPoint.kind === "location-services" &&
    sourceDraft.mode === "individual" &&
    sourcePlan.ok &&
    getAppointmentStaffRequiredTasks(sourcePlan.plan).length === 0
  ) {
    return input.staffChoiceEvidence == null && hasSameStructure(sourceDraft, workingDraft);
  }

  return (
    input.staffChoiceEvidence !== null &&
    input.staffChoiceEvidence !== undefined &&
    staffPreferencesAreProviderSupported({
      sourceDraft,
      workingDraft,
      staffChoiceEvidence: input.staffChoiceEvidence,
    })
  );
}

/** Backward-compatible Specialist-named entry for existing callers. */
export function isSupportedSpecialistAppointmentWorkingDraftTransition(input: {
  sourceDraft: AppointmentBookingDraftV1;
  workingDraft: AppointmentBookingDraftV1;
  staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
  timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
  evaluatedAtEpochMs?: number;
}) {
  return isSupportedAppointmentWorkingDraftTransition(input);
}

/**
 * Resolves the shared Step-2 -> completion boundary for both origins.
 *
 * The original Step-1 draft remains the catalog authority. Individual
 * scheduling must carry that exact appointment structure. Group scheduling
 * may contain independent service choices per participant, all of which the
 * origin scheduling context first rebuilds from the current catalog.
 * Completion then preserves that exact participant/line lineage while
 * allowing only scheduling-owned staff choices that are already validated
 * against the schedule graph and selected slot.
 */
export function resolveAppointmentCompletionContext(
  input: {
    sourceDraft: AppointmentBookingDraftV1;
    schedulingDraft: AppointmentSchedulingDraftV1;
    staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
    timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
    /** Immutable checkout/submission time used only by recovery surfaces. */
    evaluatedAtEpochMs?: number;
    /**
     * Active flows must re-resolve the current professional publication.
     * A persisted payment/submission attempt instead validates its frozen
     * authority at the immutable attempt timestamp; the next provider
     * mutation performs a separate current-publication gate.
     */
    requireCurrentProfessionalAuthority?: boolean;
    /** Exact terminal copy frozen by the immutable payment/submission attempt. */
    historicalPresentationSnapshot?: AppointmentTerminalPresentationSnapshotV1;
  },
  sources: AppointmentSchedulingContextSources = {},
): AppointmentCompletionContextResult {
  const requiresCurrentProfessionalAuthority =
    input.requireCurrentProfessionalAuthority !== false;
  const historicalPresentationSnapshot =
    !requiresCurrentProfessionalAuthority &&
    input.evaluatedAtEpochMs !== undefined
      ? validateAppointmentTerminalPresentationSnapshot(
          input.historicalPresentationSnapshot,
        )
      : null;
  let sourceContext: ResolvedAppointmentSchedulingContext | null = null;
  let historicalSourceDraft: AppointmentBookingDraftV1 | null = null;

  if (requiresCurrentProfessionalAuthority) {
    const currentSourceContext = resolveAppointmentSchedulingContext(
      input.sourceDraft,
      sources,
    );

    if (!currentSourceContext.ok) {
      return currentSourceContext;
    }

    sourceContext = currentSourceContext;
  } else {
    const historicalSourceValidation = validateAppointmentStepOneBookingDraft(
      input.sourceDraft,
    );

    if (!historicalSourceValidation.valid || !historicalPresentationSnapshot) {
      return { ok: false, reason: "invalid-draft" };
    }

    historicalSourceDraft = historicalSourceValidation.draft;
  }

  const schedulingValidation = validateAppointmentSchedulingDraft(
    input.schedulingDraft,
    { evaluatedAtEpochMs: input.evaluatedAtEpochMs },
  );

  if (!schedulingValidation.valid) {
    return { ok: false, reason: "invalid-scheduling-draft" };
  }

  const schedulingDraft = schedulingValidation.draft;
  const sourceDraft = requiresCurrentProfessionalAuthority
    ? sourceContext!.scheduling.draft
    : historicalSourceDraft!;

  if (
    historicalPresentationSnapshot &&
    !appointmentTerminalPresentationCoversDraft({
      draft: schedulingDraft.appointmentDraft,
      presentationSnapshot: historicalPresentationSnapshot,
      selectedSlot: schedulingDraft.selectedSlot,
    })
  ) {
    return { ok: false, reason: "unsupported-scheduling-transition" };
  }

  if (
    schedulingDraft.draftId !== sourceDraft.draftId ||
    schedulingDraft.sourceAppointmentDraftSchemaVersion !==
      sourceDraft.schemaVersion
  ) {
    return { ok: false, reason: "scheduling-source-mismatch" };
  }

  const timeProfessionalMutationEvidence =
    schedulingDraft.timeProfessionalMutationEvidence ?? null;
  const staffChoiceEvidence = timeProfessionalMutationEvidence
    ? null
    : appointmentDraftRequiresStaffChoiceEvidence(sourceDraft)
      ? (input.staffChoiceEvidence ?? null)
      : null;

  // A no-staff Location journey has no staff-choice descendant to consume.
  // Reject foreign evidence instead of silently dropping it during projection.
  if (
    sourceDraft.entryPoint.kind === "location-services" &&
    !appointmentDraftRequiresStaffChoiceEvidence(sourceDraft) &&
    input.staffChoiceEvidence != null
  ) {
    return { ok: false, reason: "unsupported-scheduling-transition" };
  }

  if (
    timeProfessionalMutationEvidence !== null &&
    (input.staffChoiceEvidence != null ||
      (requiresCurrentProfessionalAuthority &&
        !appointmentTimeProfessionalMutationEvidenceMatchesCurrentRuntime({
          workingDraft: schedulingDraft.appointmentDraft,
          evidence: timeProfessionalMutationEvidence,
          context: sourceContext!,
          evaluatedAtEpochMs: input.evaluatedAtEpochMs,
        })) ||
      !appointmentTimeProfessionalMutationEvidenceCoversSelectedSlot({
        draft: schedulingDraft.appointmentDraft,
        evidence: timeProfessionalMutationEvidence,
        expectedProviderIdentity: requiresCurrentProfessionalAuthority
          ? {
              providerId:
                appointmentSchedulingRuntime.timeProfessionalMutation
                  .providerId,
              evidenceVersion:
                appointmentSchedulingRuntime.timeProfessionalMutation
                  .evidenceVersion,
            }
          : {
              providerId: timeProfessionalMutationEvidence.providerId,
              evidenceVersion: timeProfessionalMutationEvidence.evidenceVersion,
            },
        evaluatedAtEpochMs: input.evaluatedAtEpochMs,
        selectedSlot: schedulingDraft.selectedSlot,
      }))
  ) {
    return {
      ok: false,
      reason: "unsupported-scheduling-transition",
    };
  }

  if (
    timeProfessionalMutationEvidence === null &&
    appointmentDraftRequiresStaffChoiceEvidence(sourceDraft) &&
    (!staffChoiceEvidence ||
      (requiresCurrentProfessionalAuthority &&
        !appointmentStaffChoiceEvidenceMatchesCurrentRuntime({
          workingDraft: schedulingDraft.appointmentDraft,
          staffChoiceEvidence,
          locationAppointmentCatalog: sourceContext!.locationAppointmentCatalog,
          presentations: sourceContext!.presentation.professionals.map(
            (professional) => ({
              userLocationId: professional.userLocationId,
              name: professional.name,
              description: professional.role ?? professional.category,
            }),
          ),
        })) ||
      !appointmentStaffChoiceEvidenceCoversSelectedSlot({
        draft: schedulingDraft.appointmentDraft,
        selectedSlot: schedulingDraft.selectedSlot,
        evidence: staffChoiceEvidence,
      }))
  ) {
    return {
      ok: false,
      reason: "unsupported-scheduling-transition",
    };
  }

  if (
    !isSupportedAppointmentWorkingDraftTransition({
      sourceDraft,
      workingDraft: schedulingDraft.appointmentDraft,
      staffChoiceEvidence,
      timeProfessionalMutationEvidence,
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
    })
  ) {
    return {
      ok: false,
      reason:
        sourceDraft.entryPoint.kind === "specialist-services" &&
        sourceDraft.mode === "individual"
          ? "scheduling-source-mismatch"
          : "unsupported-scheduling-transition",
    };
  }

  return {
    ok: true,
    specialistContext: requiresCurrentProfessionalAuthority
      ? sourceContext!.scheduling
      : buildHistoricalCompletionScheduling(historicalPresentationSnapshot!),
    presentation: requiresCurrentProfessionalAuthority
      ? sourceContext!.presentation
      : buildHistoricalCompletionPresentation(historicalPresentationSnapshot!),
    schedulingDraft,
    staffChoiceEvidence,
    timeProfessionalMutationEvidence,
  };
}

/** Backward-compatible Specialist-named entry for existing callers. */
export function resolveSpecialistAppointmentCompletionContext(
  input: {
    sourceDraft: AppointmentBookingDraftV1;
    schedulingDraft: AppointmentSchedulingDraftV1;
    staffChoiceEvidence?: AppointmentStaffChoiceEvidenceV2 | null;
    timeProfessionalMutationEvidence?: AppointmentTimeProfessionalMutationEvidenceV1 | null;
    evaluatedAtEpochMs?: number;
    requireCurrentProfessionalAuthority?: boolean;
    historicalPresentationSnapshot?: AppointmentTerminalPresentationSnapshotV1;
  },
  catalog?: SpecialistAppointmentSchedulingCatalogSource,
): SpecialistAppointmentCompletionContextResult {
  return resolveAppointmentCompletionContext(input, {
    specialist: catalog,
  });
}
